import { Component, ErrorInfo, ReactNode } from 'react'
import ErrorView from '@Shared/components/ErrorView'
import EmptyLayout from '@Layouts/EmptyLayout'
import { PrisyncError } from '@Types/PrisyncError'
import { connect } from 'react-redux'
import { RootState } from '@Store/store'
import i18n from 'i18next'
import { Location, useLocation } from 'react-router'

type ErrorBoundaryProps = {
  children: ReactNode
  location?: Location
}

type ErrorBoundaryState = {
  hasError: boolean
  error: PrisyncError | Error | null
}

function WrappedErrorBoundary(props: ErrorBoundaryProps) {
  const location = useLocation()

  return <ErrorBoundary {...props} location={location} />
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo)
  }

  componentWillUpdate(prevProps: ErrorBoundaryProps) {
    // Reset the error state when the route changes
    if (this.props?.location?.pathname !== prevProps?.location?.pathname) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (this.state.hasError) {
      const errorMessage =
        this.state.error instanceof PrisyncError ||
        this.state.error instanceof Error
          ? this.state.error.message
          : i18n.t('Prisync.ERRORS.default')

      const errorTitle = i18n.t('Prisync.ERRORS.title')

      return (
        <EmptyLayout title="Error" fullWidth narrowWidth={false}>
          <ErrorView title={errorTitle} message={errorMessage} />
        </EmptyLayout>
      )
    }

    return this.props.children
  }
}

const mapStateToProps = (state: RootState) => ({})
const mapDispatchToProps = {
  dispatch: (action: any) => action,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WrappedErrorBoundary)
